import React, { useState, useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError, API } from "@/lib/api";
import { money } from "@/lib/format";
import { toast } from "sonner";
import { GeoMap } from "@/components/GeoMap";
import { Truck, GasPump, Gauge, Plus, Trash, CaretLeft, Path, Broadcast, MapPin, Timer, Crown, CheckCircle, FileArrowUp, X } from "@phosphor-icons/react";

const TYPE_EMOJI = { bike: "🏍️", car: "🚗", truck: "🚚", van: "🚐" };
const perMile = (c) => `$${((c || 0) / 100).toFixed(2)}`;
const STATUS = {
  moving: { color: "#22c55e", label: "Moving", cls: "bg-emerald-500/15 text-emerald-600" },
  idle: { color: "#f59e0b", label: "Idle", cls: "bg-amber-500/15 text-amber-600" },
  offline: { color: "#94a3b8", label: "Offline", cls: "bg-slate-500/15 text-slate-500" },
};
const kmh = (ms) => `${Math.round((ms || 0) * 3.6)} km/h`;
const ago = (iso) => {
  if (!iso) return "never";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${Math.round(s)}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  return `${Math.round(s / 3600)}h ago`;
};
const daysLeft = (iso) => Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000));
const optLabel = (v) => `${TYPE_EMOJI[v.type] || "🚗"} ${v.name} · ${v.plate || v.type}`;

function BillingBadge({ billing }) {
  const b = billing || {};
  if (b.enabled === false) return null;
  if (b.status === "active") return <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold text-emerald-600" data-testid="billing-badge">Active · renews {new Date(b.subscribed_until).toLocaleDateString()}</span>;
  if (b.status === "trial") return <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold text-primary" data-testid="billing-badge">Trial · {daysLeft(b.trial_ends_at)}d left</span>;
  return <span className="rounded-full bg-destructive/15 px-2 py-0.5 text-[10px] font-semibold text-destructive" data-testid="billing-badge">Tracking paused</span>;
}

// ---------------------------------------------------------------- tracking plans
function PlansCard({ packages }) {
  const p = packages;
  if (!p?.enabled) return null;
  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid="fleet-plans">
      <div className="flex items-center gap-2"><Crown size={18} weight="duotone" className="text-primary" />
        <h3 className="font-display text-base font-semibold">Tracking plans</h3>
        <span className="ml-auto text-xs text-muted-foreground">{p.trial_days}-day free trial on every vehicle</span></div>
      <p className="mt-1 text-xs text-muted-foreground">Priced per vehicle / month — the more vehicles you track, the less you pay for each.</p>
      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {p.packages.map((b) => {
          const active = b.key === p.current_tier;
          return (
            <div key={b.key} data-testid={`plan-${b.key}`} className={`rounded-xl border p-3 ${active ? "border-primary bg-primary/5" : "border-border"}`}>
              <div className="flex items-center justify-between"><p className="text-sm font-semibold">{b.name}</p>{active && <CheckCircle size={15} weight="fill" className="text-primary" />}</div>
              <p className="text-xs text-muted-foreground">{b.range}</p>
              <p className="mt-2 text-lg font-semibold">${(b.price_cents / 100).toFixed(2)}</p>
              <p className="text-[11px] text-muted-foreground">{b.price_xaf.toLocaleString()} XAF / vehicle / mo</p>
            </div>
          );
        })}
      </div>
      <p className="mt-3 text-xs text-muted-foreground">You have {p.vehicle_count} vehicle{p.vehicle_count === 1 ? "" : "s"} — current tier <b className="capitalize">{p.current_tier}</b>.</p>
    </div>
  );
}

// ---------------------------------------------------------------- tracker (driver shares GPS)
function TrackerPanel({ vehicles }) {
  const [vid, setVid] = useState(vehicles[0]?.id || "");
  const [sharing, setSharing] = useState(false);
  const [last, setLast] = useState(null);
  const [err, setErr] = useState("");
  const watch = useRef(null);

  const stop = () => { if (watch.current != null) { navigator.geolocation.clearWatch(watch.current); watch.current = null; } setSharing(false); };
  useEffect(() => () => { if (watch.current != null) navigator.geolocation.clearWatch(watch.current); }, []);

  const toggle = () => {
    if (sharing) { stop(); return; }
    if (!vid) { toast.error("Pick a vehicle"); return; }
    if (!navigator.geolocation) { setErr("This device has no GPS"); return; }
    setErr("");
    watch.current = navigator.geolocation.watchPosition(async (p) => {
      const { latitude, longitude, speed } = p.coords;
      try {
        await api.post(`/fleet/tracker/${vid}/ping`, { lat: latitude, lng: longitude, speed: speed || 0 });
        setLast({ at: new Date().toISOString(), speed: speed || 0 });
      } catch (e) {
        const msg = apiError(e);
        if (e?.response?.status === 402) { setErr(msg); stop(); toast.error(msg); }
      }
    }, (e) => { setErr(e.message || "Location blocked. Allow location access to share your position."); stop(); },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 20000 });
    setSharing(true);
  };

  return (
    <div className="space-y-3 rounded-2xl border border-primary/30 bg-primary/5 p-5" data-testid="fleet-tracker">
      <div className="flex items-center gap-2"><Broadcast size={20} weight="duotone" className="text-primary" />
        <h3 className="font-display text-base font-semibold">Driver tracking</h3></div>
      <p className="text-sm text-muted-foreground">You're assigned to track {vehicles.length === 1 ? "a vehicle" : `${vehicles.length} vehicles`}. Start sharing so your company sees you live on the map.</p>
      <div className="flex flex-wrap items-center gap-2">
        <select data-testid="tracker-vehicle" value={vid} onChange={(e) => setVid(e.target.value)} disabled={sharing}
          className="rounded-xl border border-border bg-card px-3 py-2 text-sm outline-none focus:border-primary disabled:opacity-60">
          {vehicles.map((v) => <option key={v.id} value={v.id}>{optLabel(v)}</option>)}
        </select>
        <button data-testid="tracker-toggle" onClick={toggle}
          className={`rounded-full px-5 py-2 text-sm font-semibold ${sharing ? "bg-destructive text-white" : "bg-primary text-primary-foreground"}`}>
          {sharing ? "Stop sharing" : "Start sharing location"}
        </button>
        {sharing && <span className="flex items-center gap-1.5 text-sm font-medium text-emerald-600"><span className="h-2 w-2 animate-pulse rounded-full bg-emerald-500" /> Live</span>}
      </div>
      {last && <p className="text-xs text-muted-foreground" data-testid="tracker-last">Last sent {ago(last.at)} · {kmh(last.speed)}</p>}
      {err && <p className="text-xs text-destructive" data-testid="tracker-error">{err}</p>}
    </div>
  );
}

// ---------------------------------------------------------------- add / assign
function AddVehicle({ onDone }) {
  const qc = useQueryClient();
  const [f, setF] = useState({ name: "", plate: "", type: "car", make: "", model: "", year: "", fuel_type: "petrol", start_odometer: "", driver_email: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.name) { toast.error("Give the vehicle a name"); return; }
    setBusy(true);
    try {
      await api.post("/fleet/vehicles", { ...f, start_odometer: Number(f.start_odometer) || 0 });
      toast.success("Vehicle added — free trial started");
      qc.invalidateQueries({ queryKey: ["fleet-vehicles"] });
      qc.invalidateQueries({ queryKey: ["fleet-summary"] });
      qc.invalidateQueries({ queryKey: ["fleet-packages"] });
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const inp = (k, ph) => <input data-testid={`veh-${k}`} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} placeholder={ph} className="rounded-xl border border-border bg-card px-3 py-2 text-sm outline-none focus:border-primary" />;
  return (
    <div className="space-y-3 rounded-2xl border border-border bg-card p-5" data-testid="fleet-add">
      <h3 className="font-display text-base font-semibold">Add a vehicle</h3>
      <div className="grid grid-cols-2 gap-2">
        {inp("name", "Name (Delivery van 1)")}
        {inp("plate", "Plate")}
        <select data-testid="veh-type" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} className="rounded-xl border border-border bg-card px-3 py-2 text-sm outline-none focus:border-primary">
          <option value="bike">Bike</option><option value="car">Car</option><option value="van">Van</option><option value="truck">Truck</option>
        </select>
        <select data-testid="veh-fuel" value={f.fuel_type} onChange={(e) => setF({ ...f, fuel_type: e.target.value })} className="rounded-xl border border-border bg-card px-3 py-2 text-sm outline-none focus:border-primary">
          <option value="petrol">Petrol</option><option value="diesel">Diesel</option><option value="electric">Electric</option>
        </select>
        {inp("make", "Make")}
        {inp("model", "Model")}
        {inp("year", "Year")}
        {inp("start_odometer", "Start odometer (mi)")}
      </div>
      <input data-testid="veh-driver_email" value={f.driver_email} onChange={(e) => setF({ ...f, driver_email: e.target.value })} placeholder="Driver email (who shares GPS from their app)" className="w-full rounded-xl border border-border bg-card px-3 py-2 text-sm outline-none focus:border-primary" />
      <button data-testid="fleet-add-btn" onClick={submit} disabled={busy} className="rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50">{busy ? "Adding…" : "Add vehicle"}</button>
    </div>
  );
}

// ---------------------------------------------------------------- vehicle detail
function VehicleDetail({ vehicle, token, packages, onBack }) {
  const qc = useQueryClient();
  const [tab, setTab] = useState("tracking");
  const [driverEmail, setDriverEmail] = useState(vehicle.driver_email || "");
  const [log, setLog] = useState({ odometer: "", gallons: "", cost: "" });
  const [subBusy, setSubBusy] = useState(false);

  const todayStr = new Date().toISOString().slice(0, 10);
  const [day, setDay] = useState(todayStr);
  const { data: track } = useQuery({
    queryKey: ["fleet-track", vehicle.id, day],
    queryFn: async () => (await api.get(`/fleet/vehicles/${vehicle.id}/track?day=${day}`)).data,
    refetchInterval: day === todayStr ? 5000 : false, enabled: tab === "tracking",
  });
  const { data: logsData } = useQuery({
    queryKey: ["fleet-logs", vehicle.id],
    queryFn: async () => (await api.get(`/fleet/vehicles/${vehicle.id}/logs`)).data, enabled: tab === "fuel",
  });
  const m = logsData?.metrics;
  const bstatus = vehicle.billing?.status || "inactive";
  const trackable = bstatus !== "inactive";
  const liveStatus = track?.status || vehicle.track?.status;
  const st = STATUS[(trackable && liveStatus) || "offline"];
  const cur = (track?.current?.lat != null) ? track.current : { lat: vehicle.track?.lat, lng: vehicle.track?.lng, speed: vehicle.track?.speed };
  const markers = trackable && cur?.lat != null ? [{ key: vehicle.id, lat: cur.lat, lng: cur.lng, color: st.color }] : [];
  const routeGeo = trackable && (track?.points?.length || 0) >= 2 ? { type: "LineString", coordinates: track.points } : null;

  const subscribe = async () => {
    setSubBusy(true);
    try {
      const r = (await api.post(`/fleet/vehicles/${vehicle.id}/subscribe`)).data;
      toast.success(`Tracking activated (${r.tier.name})`);
      qc.invalidateQueries({ queryKey: ["fleet-vehicles"] }); qc.invalidateQueries({ queryKey: ["fleet-summary"] });
    } catch (e) { toast.error(apiError(e)); } finally { setSubBusy(false); }
  };
  const saveDriver = async () => {
    try {
      await api.put(`/fleet/vehicles/${vehicle.id}`, { ...vehicle, driver_email: driverEmail });
      toast.success("Driver assigned"); qc.invalidateQueries({ queryKey: ["fleet-vehicles"] });
    } catch (e) { toast.error(apiError(e)); }
  };
  const addLog = async () => {
    if (!log.odometer || !log.gallons) { toast.error("Enter odometer and gallons"); return; }
    try {
      await api.post(`/fleet/vehicles/${vehicle.id}/logs`, { odometer: Number(log.odometer), gallons: Number(log.gallons), cost_cents: Math.round(Number(log.cost || 0) * 100) });
      toast.success("Fill-up logged"); setLog({ odometer: "", gallons: "", cost: "" });
      qc.invalidateQueries({ queryKey: ["fleet-logs", vehicle.id] }); qc.invalidateQueries({ queryKey: ["fleet-vehicles"] });
    } catch (e) { toast.error(apiError(e)); }
  };
  const delLog = async (id) => { try { await api.delete(`/fleet/logs/${id}`); qc.invalidateQueries({ queryKey: ["fleet-logs", vehicle.id] }); } catch (e) { toast.error(apiError(e)); } };
  const delVehicle = async () => {
    try { await api.delete(`/fleet/vehicles/${vehicle.id}`); toast.success("Vehicle removed"); qc.invalidateQueries({ queryKey: ["fleet-vehicles"] }); qc.invalidateQueries({ queryKey: ["fleet-summary"] }); onBack(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const uploadLog = async (f) => {
    if (!f) return;
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = (await api.post(`/fleet/vehicles/${vehicle.id}/track/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
      toast.success(`Imported ${r.count} points — showing ${r.day}`); setDay(r.day);
      qc.invalidateQueries({ queryKey: ["fleet-track", vehicle.id, r.day] });
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="fleet-detail">
      <button data-testid="fleet-back" onClick={onBack} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><CaretLeft size={16} /> Fleet</button>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent text-2xl">{TYPE_EMOJI[vehicle.type] || "🚗"}</div>
          <div>
            <div className="flex items-center gap-2"><h2 className="font-display text-xl font-semibold">{vehicle.name}</h2>
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${st.cls}`} data-testid="detail-status">{st.label}</span>
              <BillingBadge billing={vehicle.billing} /></div>
            <p className="text-xs text-muted-foreground">{vehicle.plate} · {vehicle.make} {vehicle.model} {vehicle.year}</p>
          </div>
        </div>
        <button data-testid="veh-delete" onClick={delVehicle} className="flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs font-semibold text-muted-foreground hover:text-destructive"><Trash size={14} /> Remove</button>
      </div>

      {bstatus === "inactive" && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-destructive/30 bg-destructive/5 p-4" data-testid="activate-banner">
          <p className="text-sm text-muted-foreground">Tracking is paused for this vehicle. Activate a monthly plan to bring it back online — its history is kept.</p>
          <button data-testid="activate-btn" onClick={subscribe} disabled={subBusy} className="rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50">{subBusy ? "Activating…" : "Activate tracking"}</button>
        </div>
      )}

      <div className="flex gap-2">
        {[["tracking", "Live tracking"], ["fuel", "Fuel efficiency"]].map(([k, l]) => (
          <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)}
            className={`rounded-full px-4 py-1.5 text-sm font-semibold ${tab === k ? "bg-primary text-primary-foreground" : "border border-border text-muted-foreground"}`}>{l}</button>
        ))}
      </div>

      {tab === "tracking" && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Metric icon={MapPin} label="Status" value={st.label} testid="track-status-tile" />
            <Metric icon={Gauge} label="Speed" value={kmh(cur?.speed ?? vehicle.track?.speed)} />
            <Metric icon={Path} label={day === todayStr ? "Distance today" : "Distance"} value={`${((track?.distance_m || (day === todayStr ? vehicle.track?.distance_today_m : 0) || 0) / 1000).toFixed(1)} km`} testid="track-distance" />
            <Metric icon={Timer} label={day === todayStr ? "Last seen" : "Log points"} value={day === todayStr ? ago(track?.last_seen || vehicle.track?.updated_at) : (track?.count ?? 0)} />
          </div>
          {markers.length > 0 || routeGeo
            ? <GeoMap token={token} markers={markers} routeGeo={routeGeo} />
            : <div className="rounded-2xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground" data-testid="track-empty">
                {trackable ? "No location yet. Assign a driver below — when they start sharing from the app, their live position and route appear here." : "Tracking is paused. Activate a plan to see this vehicle live."}</div>}
          {routeGeo && <p className="text-xs text-muted-foreground">Blue line = route replay for {day} ({track.count} points · max {kmh(track.max_speed_ms)}).</p>}

          <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-border bg-card p-4" data-testid="route-log">
            <span className="text-sm font-semibold">Route history</span>
            <input data-testid="track-day" type="date" value={day} onChange={(e) => setDay(e.target.value)}
              className="rounded-xl border border-border bg-background px-3 py-1.5 text-sm text-muted-foreground outline-none focus:border-primary" />
            <label data-testid="track-upload" className="flex cursor-pointer items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-semibold hover:border-primary">
              <FileArrowUp size={14} /> Upload GPS log (GPX / CSV / JSON)
              <input type="file" accept=".gpx,.csv,.json,application/json,text/csv" className="hidden" onChange={(e) => uploadLog(e.target.files?.[0])} />
            </label>
            <span className="text-xs text-muted-foreground">For SIM-less loggers — upload the day's file to replay its route.</span>
          </div>

          <div className="rounded-2xl border border-border bg-card p-5" data-testid="assign-driver">
            <h3 className="font-display text-base font-semibold">Assigned driver</h3>
            <p className="mt-1 text-xs text-muted-foreground">This person opens iYeneh on their phone and taps “Start sharing location”.</p>
            <div className="mt-3 flex gap-2">
              <input data-testid="assign-email" value={driverEmail} onChange={(e) => setDriverEmail(e.target.value)} placeholder="driver@email.com" className="flex-1 rounded-xl border border-border bg-card px-3 py-2 text-sm outline-none focus:border-primary" />
              <button data-testid="assign-save" onClick={saveDriver} className="rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground">Save</button>
            </div>
          </div>

          <DeviceCard vehicle={vehicle} packages={packages} />
        </div>
      )}

      {tab === "fuel" && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Metric icon={Gauge} label="Odometer" value={`${vehicle.odometer} mi`} />
            <Metric icon={Path} label="Tracked miles" value={`${m?.total_miles ?? 0} mi`} />
            <Metric icon={GasPump} label="Cost / mile" value={perMile(m?.cost_per_mile_cents)} testid="fleet-cpm" />
            <Metric icon={Truck} label="MPG" value={m?.mpg ?? 0} />
          </div>
          <div className="rounded-2xl border border-border bg-card p-5">
            <h3 className="font-display text-base font-semibold">Log a fuel fill-up</h3>
            <div className="mt-3 grid grid-cols-3 gap-2">
              <input data-testid="log-odometer" type="number" value={log.odometer} onChange={(e) => setLog({ ...log, odometer: e.target.value })} placeholder="Odometer (mi)" className="rounded-xl border border-border bg-card px-3 py-2 text-sm outline-none focus:border-primary" />
              <input data-testid="log-gallons" type="number" step="0.1" value={log.gallons} onChange={(e) => setLog({ ...log, gallons: e.target.value })} placeholder="Gallons" className="rounded-xl border border-border bg-card px-3 py-2 text-sm outline-none focus:border-primary" />
              <input data-testid="log-cost" type="number" step="0.01" value={log.cost} onChange={(e) => setLog({ ...log, cost: e.target.value })} placeholder="Cost $" className="rounded-xl border border-border bg-card px-3 py-2 text-sm outline-none focus:border-primary" />
            </div>
            <button data-testid="log-add-btn" onClick={addLog} className="mt-3 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground">Add fill-up</button>
            <div className="mt-4 space-y-2">
              {(logsData?.logs || []).length === 0 && <p className="text-sm text-muted-foreground">No fill-ups yet. Add two or more to see cost per mile.</p>}
              {(logsData?.logs || []).map((l) => (
                <div key={l.id} className="flex items-center justify-between rounded-xl bg-background px-3 py-2 text-sm">
                  <span>{l.odometer} mi · {l.gallons} gal · {money(l.cost_cents)}</span>
                  <button data-testid={`log-del-${l.id}`} onClick={() => delLog(l.id)} className="text-muted-foreground hover:text-destructive"><Trash size={15} /></button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Metric({ icon: Icon, label, value, testid }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4" data-testid={testid}>
      <Icon size={18} className="text-primary" weight="duotone" />
      <p className="mt-2 text-lg font-semibold">{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
}

// ---------------------------------------------------------------- in-vehicle GPS box (paid add-on)
function DeviceCard({ vehicle, packages }) {
  const qc = useQueryClient();
  const d = vehicle.device || {};
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState("");
  const addon = packages?.device_addon_cents || 0;
  const ingestUrl = d.token ? `${API}/fleet/device/${d.token}/ping` : "";
  const getUrl = d.token ? `${ingestUrl}?lat=4.0511&lng=9.7679&speed=32` : "";

  const act = async (path, msg) => { setBusy(true); try { await api.post(`/fleet/vehicles/${vehicle.id}/device/${path}`); toast.success(msg); qc.invalidateQueries({ queryKey: ["fleet-vehicles"] }); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); } };
  const copy = (text, k) => { navigator.clipboard?.writeText(text); setCopied(k); setTimeout(() => setCopied(""), 1500); };

  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid="device-card">
      <div className="flex items-center gap-2">
        <MapPin size={18} weight="duotone" className="text-primary" />
        <h3 className="font-display text-base font-semibold">In-vehicle GPS box</h3>
        {d.enabled && <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold text-emerald-600">Primary</span>}
        {d.enabled && d.source === "device" && <span className="flex items-center gap-1 text-xs font-medium text-emerald-600"><span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" /> receiving</span>}
      </div>

      {!d.enabled ? (
        <>
          <p className="mt-2 text-sm text-muted-foreground">Track the vehicle itself with a GPS box — works with the engine off and needs no driver phone. Paid add-on: <b>${(addon / 100).toFixed(2)}/vehicle/month</b> on top of your plan.</p>
          <button data-testid="device-enable" onClick={() => act("enable", "GPS box enabled")} disabled={busy} className="mt-3 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50">{busy ? "…" : `Add GPS box (+$${(addon / 100).toFixed(2)}/mo)`}</button>
        </>
      ) : (
        <>
          <p className="mt-2 text-sm text-muted-foreground">Point any GPS tracker — or the free <b>OwnTracks</b> / <b>Traccar Client</b> app on a phone left in the vehicle — at this URL. It updates the live map automatically; the driver phone stays as backup.</p>
          <div className="mt-3 space-y-2">
            <Field label="Device token" value={d.token} onCopy={() => copy(d.token, "tok")} copied={copied === "tok"} testid="device-token" />
            <Field label="Ingest URL (POST JSON {lat,lng,speed})" value={ingestUrl} onCopy={() => copy(ingestUrl, "url")} copied={copied === "url"} testid="device-url" />
            <Field label="Simple URL (GET, for basic trackers)" value={getUrl} onCopy={() => copy(getUrl, "get")} copied={copied === "get"} testid="device-geturl" />
          </div>
          <div className="mt-3 flex gap-2">
            <button data-testid="device-regen" onClick={() => act("regenerate", "New token generated")} disabled={busy} className="rounded-full border border-border px-4 py-2 text-xs font-semibold text-muted-foreground hover:text-foreground disabled:opacity-50">Regenerate token</button>
            <button data-testid="device-disable" onClick={() => act("disable", "GPS box disabled")} disabled={busy} className="rounded-full border border-border px-4 py-2 text-xs font-semibold text-muted-foreground hover:text-destructive disabled:opacity-50">Disable</button>
          </div>
        </>
      )}
    </div>
  );
}

function Field({ label, value, onCopy, copied, testid }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <div className="mt-1 flex items-center gap-2">
        <code data-testid={testid} className="flex-1 truncate rounded-lg bg-background px-3 py-2 text-xs">{value}</code>
        <button onClick={onCopy} className="rounded-lg border border-border px-3 py-2 text-xs font-semibold hover:border-primary">{copied ? "Copied" : "Copy"}</button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- business owner registration
const BIZ_DOC_DEFS = [
  { key: "business_license", label: "Business license" },
  { key: "owner_id", label: "Owner photo ID" },
  { key: "tax_certificate", label: "Tax certificate" },
];

function BizDocUploader({ documents = [], onUploaded }) {
  const [busy, setBusy] = useState("");
  const upload = async (key, file) => {
    if (!file) return;
    setBusy(key);
    try {
      const fd = new FormData(); fd.append("doc_type", key); fd.append("file", file);
      await api.post("/fleet/business/document", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded"); onUploaded?.();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  return (
    <div className="space-y-2" data-testid="biz-docs">
      {BIZ_DOC_DEFS.map((d) => {
        const done = documents.includes(d.key);
        return (
          <label key={d.key} data-testid={`biz-doc-${d.key}`} className="flex cursor-pointer items-center justify-between gap-3 rounded-xl border border-border bg-card p-3 text-sm hover:border-primary/40">
            <span className="flex items-center gap-2">{done ? <CheckCircle size={18} weight="fill" className="text-emerald-600" /> : <FileArrowUp size={18} className="text-muted-foreground" />}{d.label}</span>
            <span className="rounded-full bg-secondary px-3 py-1 text-xs font-semibold">{busy === d.key ? "Uploading…" : done ? "Replace" : "Upload"}</span>
            <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => upload(d.key, e.target.files?.[0])} />
          </label>
        );
      })}
    </div>
  );
}

function FleetBusinessRegister({ business, onChanged }) {
  const [f, setF] = useState({
    business_name: business?.business_name || "", reg_number: business?.reg_number || "",
    owner_name: business?.owner_name || "", owner_phone: business?.owner_phone || "",
    contact_email: business?.contact_email || "", address: business?.address || "",
  });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.business_name || !f.owner_name) { toast.error("Add the business name and owner name"); return; }
    setBusy(true);
    try { await api.post("/fleet/business", f); toast.success("Registered — upload your documents for approval"); onChanged(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const inp = (k, ph) => <input data-testid={`biz-${k}`} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} placeholder={ph} className="w-full rounded-xl border border-border bg-card px-3 py-2.5 text-sm outline-none focus:border-primary" />;
  const registered = !!business;
  const approved = business?.approval_status === "approved";
  const rejected = business?.approval_status === "rejected";
  return (
    <FleetWrap>
      <div className="mx-auto max-w-md space-y-4">
        {registered && !approved && (
          <div className={`rounded-2xl border p-5 ${rejected ? "border-destructive/40 bg-destructive/5" : "border-amber-500/40 bg-amber-500/5"}`} data-testid="biz-gate">
            <div className="flex items-center gap-2">{rejected ? <X size={20} className="text-destructive" /> : <Timer size={20} className="text-amber-600" weight="fill" />}
              <h3 className="font-display text-base font-semibold">{rejected ? "Not approved" : "Under review"}</h3></div>
            <p className="mt-1 text-sm text-muted-foreground">{rejected ? (business.inspection?.notes || "Your business wasn't approved. Re-upload clear documents for another review.") : "Thanks! Upload your documents below. A community inspector approves your business before you can add & track vehicles."}</p>
          </div>
        )}
        <div className="space-y-3 rounded-2xl border border-border bg-card p-6" data-testid="biz-register">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent text-primary"><Truck size={22} weight="duotone" /></div>
            <div><h2 className="font-display text-lg font-semibold">Business Owner Registration</h2><p className="text-sm text-muted-foreground">Register your company to track its vehicles.</p></div>
          </div>
          {inp("owner_name", "Business owner full name")}
          {inp("business_name", "Business name")}
          <div className="grid grid-cols-2 gap-3">{inp("reg_number", "Business reg. / Tax ID")}{inp("owner_phone", "Owner phone")}</div>
          {inp("contact_email", "Contact email")}
          {inp("address", "Business address")}
          <button data-testid="biz-register-btn" onClick={submit} disabled={busy} className="w-full rounded-full bg-primary py-3 text-sm font-semibold text-primary-foreground disabled:opacity-50">{busy ? "Saving…" : registered ? "Update details" : "Register business"}</button>
        </div>
        {registered && !approved && (
          <div><p className="mb-2 text-sm font-semibold">Owner &amp; business documents</p><BizDocUploader documents={business.documents} onUploaded={onChanged} /></div>
        )}
      </div>
    </FleetWrap>
  );
}

// ---------------------------------------------------------------- page
export default function Fleet() {
  const qc = useQueryClient();
  const [sel, setSel] = useState(null);
  const [adding, setAdding] = useState(false);
  const { data: config } = useQuery({ queryKey: ["fleet-config"], queryFn: async () => (await api.get("/fleet/config")).data });
  const { data: bizData, refetch: refetchBiz, isLoading: bizLoading } = useQuery({ queryKey: ["fleet-business"], queryFn: async () => (await api.get("/fleet/business")).data.business });
  const { data: packages } = useQuery({ queryKey: ["fleet-packages"], queryFn: async () => (await api.get("/fleet/packages")).data });
  const { data: tracker } = useQuery({ queryKey: ["fleet-tracker"], queryFn: async () => (await api.get("/fleet/tracker")).data.vehicles });
  const { data: vehicles } = useQuery({ queryKey: ["fleet-vehicles"], queryFn: async () => (await api.get("/fleet/vehicles")).data.vehicles, refetchInterval: 6000 });
  const { data: summary } = useQuery({ queryKey: ["fleet-summary"], queryFn: async () => (await api.get("/fleet/summary")).data, refetchInterval: 6000 });
  const token = config?.mapbox_token;

  const business = bizData;
  if (bizLoading) return <FleetWrap><div className="text-muted-foreground">Loading…</div></FleetWrap>;
  if (!business || business.approval_status !== "approved") {
    return <FleetBusinessRegister business={business} onChanged={() => { refetchBiz(); qc.invalidateQueries({ queryKey: ["fleet-config"] }); }} />;
  }

  const activate = async (id, e) => {
    e.stopPropagation();
    try {
      const r = (await api.post(`/fleet/vehicles/${id}/subscribe`)).data;
      toast.success(`Tracking activated (${r.tier.name})`);
      qc.invalidateQueries({ queryKey: ["fleet-vehicles"] }); qc.invalidateQueries({ queryKey: ["fleet-summary"] });
    } catch (err) { toast.error(apiError(err)); }
  };

  if (sel) {
    const live = (vehicles || []).find((v) => v.id === sel.id) || sel;
    return <FleetWrap><VehicleDetail vehicle={live} token={token} packages={packages} onBack={() => setSel(null)} /></FleetWrap>;
  }

  const mapMarkers = (vehicles || []).filter((v) => v.billing?.status !== "inactive" && v.track?.lat != null)
    .map((v) => ({ key: v.id, lat: v.track.lat, lng: v.track.lng, color: STATUS[v.track.status].color }));

  return (
    <FleetWrap>
      {(tracker || []).length > 0 && <TrackerPanel vehicles={tracker} />}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Metric icon={Truck} label="Vehicles" value={summary?.vehicles ?? 0} />
        <Metric icon={Broadcast} label="Moving now" value={summary?.moving ?? 0} testid="summary-moving" />
        <Metric icon={Path} label="Distance today" value={`${summary?.distance_today_km ?? 0} km`} />
        <Metric icon={Gauge} label="Avg cost / mile" value={perMile(summary?.cost_per_mile_cents)} testid="fleet-avg-cpm" />
      </div>

      {token && mapMarkers.length > 0 && (
        <div data-testid="fleet-live-map"><GeoMap token={token} markers={mapMarkers} /></div>
      )}

      <PlansCard packages={packages} />

      <div className="flex items-center justify-between">
        <h2 className="font-display text-lg font-semibold">Your vehicles</h2>
        <button data-testid="fleet-toggle-add" onClick={() => setAdding((a) => !a)} className="flex items-center gap-1 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground"><Plus size={15} weight="bold" /> Add vehicle</button>
      </div>
      {adding && <AddVehicle onDone={() => setAdding(false)} />}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {(vehicles || []).length === 0 && !adding && <p className="rounded-2xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground sm:col-span-3">No vehicles yet. Add one, assign a driver, and track its movement live on the map.</p>}
        {(vehicles || []).map((v) => {
          const trackable = v.billing?.status !== "inactive";
          const s = STATUS[(trackable && v.track?.status) || "offline"];
          return (
            <div key={v.id} role="button" tabIndex={0} data-testid={`fleet-veh-${v.id}`} onClick={() => setSel(v)}
              onKeyDown={(e) => { if (e.key === "Enter") setSel(v); }}
              className="cursor-pointer rounded-2xl border border-border bg-card p-4 text-left transition-all hover:-translate-y-0.5 hover:border-primary">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2"><span className="text-2xl">{TYPE_EMOJI[v.type] || "🚗"}</span><div><p className="font-semibold">{v.name}</p><p className="text-xs text-muted-foreground">{v.plate || v.type}</p></div></div>
                <span className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${s.cls}`}><span className="h-1.5 w-1.5 rounded-full" style={{ background: s.color }} />{s.label}</span>
              </div>
              <div className="mt-3 flex items-center justify-between text-sm">
                <BillingBadge billing={v.billing} />
                <span className="font-semibold">{((v.track?.distance_today_m || 0) / 1000).toFixed(1)} km today</span>
              </div>
              {v.billing?.status === "inactive" && v.billing?.enabled !== false && (
                <button data-testid={`fleet-activate-${v.id}`} onClick={(e) => activate(v.id, e)} className="mt-3 w-full rounded-full bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground">Activate tracking</button>
              )}
            </div>
          );
        })}
      </div>
    </FleetWrap>
  );
}

function FleetWrap({ children }) {
  return (
    <div className="space-y-6" data-testid="fleet-page">
      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">Fleet</h1>
        <p className="mt-1 text-muted-foreground">Track your company vehicles live — positions, routes, speed and fuel efficiency.</p>
      </div>
      {children}
    </div>
  );
}
